import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Building2, Award, Loader2, CheckCircle, Sparkles, ArrowRight, Clock, Eye, EyeOff, X, Check, Edit2, Save } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  extractDegreePrefix,
  calculateScore,
  SEARCH_CONFIG,
  getPrefixVariant
} from '../utils/rncpSearchUtils';

const Onboarding3 = () => {
  const navigate = useNavigate();

  // États (identiques à TestFusionOnboarding)
  const [siret, setSiret] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('welcome'); // welcome, formations, enrichment, analysis, comparison
  const [organism, setOrganism] = useState(null);
  const [formations, setFormations] = useState([]);
  const [enrichedFormations, setEnrichedFormations] = useState([]);
  const [fusionResults, setFusionResults] = useState([]);
  const [similarityThreshold, setSimilarityThreshold] = useState(66);
  const [editingRncp, setEditingRncp] = useState(null);
  const [manualRncp, setManualRncp] = useState('');
  const [comparisonData, setComparisonData] = useState(null);
  const [showOnlyFused, setShowOnlyFused] = useState(true);

  // Slides pour la galerie (du OnboardingModal)
  const slides = [
    {
      title: "Analysez vos formations",
      description: "Identifiez automatiquement les opportunités de fusion"
    },
    {
      title: "Optimisez votre offre",
      description: "Réduisez les doublons et gagnez en efficacité"
    },
    {
      title: "Économisez du temps",
      description: "Automatisez l'analyse de vos certifications RNCP"
    }
  ];

  const [currentSlide, setCurrentSlide] = useState(0);

  // Animation automatique des slides
  React.useEffect(() => {
    if (step !== 'welcome') return;
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [step, slides.length]);

  // Fonction de scoring client
  const calculateClientScore = (query, title, rncpCode = '') => {
    if (!query || !title) return 0;
    return calculateScore(query, title, rncpCode, true);
  };

  // Formatage SIRET
  const formatSiret = (value) => {
    const numbers = value.replace(/\s/g, '');
    if (numbers.length <= 14) {
      return numbers.replace(/(\d{3})(\d{3})?(\d{3})?(\d{2})?(\d{3})?/, (match, p1, p2, p3, p4, p5) => {
        let result = p1;
        if (p2) result += ' ' + p2;
        if (p3) result += ' ' + p3;
        if (p4) result += ' ' + p4;
        if (p5) result += ' ' + p5;
        return result;
      });
    }
    return value;
  };

  // Étape 1: Rechercher l'organisme et les formations (EXACT de TestFusionOnboarding)
  const handleSearchOrganism = async () => {
    const cleanSiret = siret.replace(/\s/g, '');

    if (!cleanSiret || cleanSiret.length !== 14 || !/^\d{14}$/.test(cleanSiret)) {
      toast.error('Le SIRET doit contenir exactement 14 chiffres');
      return;
    }

    setLoading(true);
    try {
      // Scraper Intercariforef
      const scrapeResponse = await fetch(
        `${import.meta.env.VITE_API_URL}/intercariforef/scrape`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ siret: cleanSiret })
        }
      );

      const scrapeData = await scrapeResponse.json();

      if (!scrapeData.success || !scrapeData.data) {
        toast.error('Organisme non trouvé');
        return;
      }

      setOrganism({
        name: scrapeData.data.organism?.name || 'Organisme trouvé',
        siret: cleanSiret,
        totalFormations: scrapeData.data.totalFormations
      });

      // Récupérer les formations
      const formationsResponse = await fetch(
        `${import.meta.env.VITE_API_URL}/intercariforef/formations`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            siret: cleanSiret,
            scrapeSource: 'intercariforef'
          })
        }
      );

      const formationsData = await formationsResponse.json();

      if (formationsData.success && formationsData.formations) {
        // Dédupliquer
        const seen = new Set();
        const uniqueFormations = formationsData.formations.filter(f => {
          const key = f.title.toLowerCase();
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });

        // Rechercher les RNCP SÉQUENTIELLEMENT (pas tous en parallèle pour éviter surcharge API)
        const formationsWithRncp = [];

        for (let i = 0; i < uniqueFormations.length; i++) {
          const formation = uniqueFormations[i];

          try {
            const headers = {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            };
            const baseUrl = `${import.meta.env.VITE_API_URL}/kit-apprentissage/search/intitule`;

            const { prefix, coreQuery} = extractDegreePrefix(formation.title);
            const apiSearchTerm = formation.title;

            const searchPromises = [];

            // TYPE 1: Recherche complète
            searchPromises.push(
              fetch(`${baseUrl}?q=${encodeURIComponent(apiSearchTerm)}&limit=100`, { headers })
                .then(r => r.json())
                .catch(() => ({ success: false, data: [] }))
            );

            // TYPE 2: Sans préfixe
            if (prefix && coreQuery && coreQuery !== apiSearchTerm) {
              searchPromises.push(
                fetch(`${baseUrl}?q=${encodeURIComponent(coreQuery)}&limit=100`, { headers })
                  .then(r => r.json())
                  .catch(() => ({ success: false, data: [] }))
              );
            }

            // TYPE 5: 2 premiers mots spécifiques
            const { stopWords, genericWords } = SEARCH_CONFIG;
            const queryForKeywords = (prefix ? coreQuery : apiSearchTerm)
              .replace(/-/g, ' ')
              .replace(/[,;:.!?()]/g, ' ')
              .replace(/\s+/g, ' ')
              .trim();

            const words = queryForKeywords.split(' ')
              .filter(w => w.length > 3 && !stopWords.includes(w.toLowerCase()))
              .map((w, index) => ({
                word: w,
                isGeneric: genericWords.includes(w.toLowerCase())
              }));

            const specificWords = words.filter(w => !w.isGeneric);

            if (specificWords.length >= 2) {
              const keyWords2 = specificWords.slice(0, 2).map(w => w.word).join(' ');
              if (keyWords2 && keyWords2 !== apiSearchTerm && keyWords2 !== coreQuery) {
                searchPromises.push(
                  fetch(`${baseUrl}?q=${encodeURIComponent(keyWords2)}&limit=100`, { headers })
                    .then(r => r.json())
                    .catch(() => ({ success: false, data: [] }))
                );
              }
            }

            // TYPE 6: Préfixe + 1er mot
            if (prefix && specificWords.length >= 1) {
              const queryWithPrefix = `${prefix} ${specificWords[0].word}`;
              searchPromises.push(
                fetch(`${baseUrl}?q=${encodeURIComponent(queryWithPrefix)}&limit=100`, { headers })
                  .then(r => r.json())
                  .catch(() => ({ success: false, data: [] }))
              );
            }

            // TYPE 7: Variante de préfixe
            if (prefix) {
              const prefixVariant = getPrefixVariant(prefix);
              if (prefixVariant && prefixVariant !== prefix.toUpperCase()) {
                const queryWithVariant = `${prefixVariant} ${coreQuery}`;
                searchPromises.push(
                  fetch(`${baseUrl}?q=${encodeURIComponent(queryWithVariant)}&limit=100`, { headers })
                    .then(r => r.json())
                    .catch(() => ({ success: false, data: [] }))
                );
              }
            }

            // Exécuter toutes les recherches en parallèle (pour CETTE formation uniquement)
            const responses = await Promise.all(searchPromises);

            // Combiner et dédupliquer par RNCP
            let allResults = [];
            const seenRncps = new Set();

            responses.forEach(resp => {
              if (resp.success && resp.data) {
                resp.data.forEach(result => {
                  if (!seenRncps.has(result.code_rncp)) {
                    seenRncps.add(result.code_rncp);
                    allResults.push(result);
                  }
                });
              }
            });

            // Scorer et trier
            const scoredResults = allResults.map(result => ({
              ...result,
              score: calculateClientScore(formation.title, result.title, result.code_rncp)
            }));

            scoredResults.sort((a, b) => b.score - a.score);

            // Retourner formation avec RNCP suggéré
            const bestMatch = scoredResults[0];
            formationsWithRncp.push({
              ...formation,
              suggestedRncp: bestMatch?.code_rncp || null,
              score: bestMatch?.score || 0,
              matches: scoredResults.slice(0, 5)
            });

            // Délai de 100ms entre chaque formation pour ne pas surcharger l'API
            if (i < uniqueFormations.length - 1) {
              await new Promise(resolve => setTimeout(resolve, 100));
            }
          } catch (error) {
            console.error('Erreur recherche:', error);
            formationsWithRncp.push({
              ...formation,
              suggestedRncp: null,
              score: 0,
              matches: []
            });
          }
        }

        setFormations(formationsWithRncp);
        setStep('formations');
        toast.success(`${formationsWithRncp.length} formations trouvées`);
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors de la recherche');
    } finally {
      setLoading(false);
    }
  };

  // Éditer manuellement le RNCP
  const handleEditRncp = (index) => {
    setEditingRncp(index);
    setManualRncp(formations[index].suggestedRncp || '');
  };

  const handleSaveRncp = (index) => {
    const updated = [...formations];
    updated[index].suggestedRncp = manualRncp;
    updated[index].score = 100;
    setFormations(updated);
    setEditingRncp(null);
    setManualRncp('');
  };

  // Étape 2: Enrichir avec les compétences (EXACT de TestFusionOnboarding)
  const handleEnrichFormations = async () => {
    setLoading(true);
    setStep('enrichment');

    try {
      const enriched = [];
      const formationsToEnrich = formations.filter(f => f.suggestedRncp);

      for (let i = 0; i < formationsToEnrich.length; i++) {
        const formation = formationsToEnrich[i];
        const fullRncp = formation.suggestedRncp.toUpperCase().startsWith('RNCP')
          ? formation.suggestedRncp
          : `RNCP${formation.suggestedRncp}`;

        try {
          const numeroRncp = fullRncp.replace(/^RNCP/i, '');

          const response = await fetch(
            `${import.meta.env.VITE_API_URL}/rncp-scraping/scrape`,
            {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ numeroRncp })
            }
          );

          const data = await response.json();

          if (data.success && data.data) {
            let competences = [];
            let blocks = [];

            if (data.data.blocsCompetences && Array.isArray(data.data.blocsCompetences)) {
              blocks = data.data.blocsCompetences.map(bloc => ({
                code: bloc.code,
                libelle: bloc.libelle,
                competences: bloc.competences || []
              }));

              data.data.blocsCompetences.forEach(bloc => {
                if (bloc.competences && Array.isArray(bloc.competences)) {
                  bloc.competences.forEach(comp => {
                    competences.push(comp);
                  });
                }
              });
            }

            enriched.push({
              ...formation,
              rncpCode: fullRncp,
              competences,
              blocks,
              enriched: true
            });
          }
        } catch (err) {
          console.error('Erreur enrichissement:', err);
        }
      }

      setEnrichedFormations(enriched);
      toast.success(`${enriched.length} formations enrichies`);

      // Lancer automatiquement l'analyse
      await handleAnalyzeFusions(enriched);

    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors de l\'enrichissement');
    } finally {
      setLoading(false);
    }
  };

  // Étape 3: Analyser les fusions (EXACT de TestFusionOnboarding - Algorithme Jaccard)
  const handleAnalyzeFusions = async (enrichedData = enrichedFormations) => {
    setLoading(true);
    setStep('analysis');

    try {
      // Algorithme de Jaccard
      const jaccard = (str1, str2) => {
        const set1 = new Set(str1.toLowerCase().split(/\s+/));
        const set2 = new Set(str2.toLowerCase().split(/\s+/));
        const intersection = new Set([...set1].filter(x => set2.has(x)));
        const union = new Set([...set1, ...set2]);
        return (intersection.size / union.size) * 100;
      };

      const competencesList = [];
      enrichedData.forEach(formation => {
        formation.competences.forEach(comp => {
          competencesList.push({
            text: comp,
            certification_id: formation.rncpCode,
            certificationTitle: formation.title
          });
        });
      });

      const fused = [];
      const processed = new Set();

      competencesList.forEach((comp1, i) => {
        if (processed.has(i)) return;

        const similar = [comp1];
        const similarIndices = [i];

        competencesList.forEach((comp2, j) => {
          if (i === j || processed.has(j)) return;
          if (comp1.certification_id === comp2.certification_id) return;

          const similarity = jaccard(comp1.text, comp2.text);
          if (similarity >= similarityThreshold) {
            similar.push(comp2);
            similarIndices.push(j);
          }
        });

        similarIndices.forEach(idx => processed.add(idx));

        if (similar.length > 1) {
          const avgSimilarity = Math.round(
            similar.reduce((sum, s, idx) => {
              if (idx === 0) return sum;
              return sum + jaccard(comp1.text, s.text);
            }, 0) / (similar.length - 1)
          );

          fused.push({
            description: comp1.text,
            count: similar.length,
            isMerged: true,
            avgSimilarity,
            sources: similar.map(s => ({
              text: s.text,
              rncp: s.certification_id,
              formation: s.certificationTitle
            }))
          });
        }
      });

      const sorted = fused
        .filter(f => f.isMerged)
        .sort((a, b) => b.count - a.count);

      setFusionResults(sorted);

      const mergedCount = fused.filter(f => f.isMerged).length;
      toast.success(`Analyse terminée : ${mergedCount} compétences fusionnées`);

      // Préparer la comparaison automatiquement
      if (sorted.length > 0 && enrichedData.length >= 2) {
        await handlePrepareComparison(enrichedData, sorted);
      } else {
        setStep('analysis');
      }

    } catch (error) {
      console.error('Erreur analyse:', error);
      toast.error('Erreur lors de l\'analyse');
    } finally {
      setLoading(false);
    }
  };

  // Étape 4: Préparer la comparaison (EXACT de TestFusionOnboarding)
  const handlePrepareComparison = (enrichedData, fusionData) => {
    const formationPairs = [];

    for (let i = 0; i < enrichedData.length; i++) {
      for (let j = i + 1; j < enrichedData.length; j++) {
        const formation1 = enrichedData[i];
        const formation2 = enrichedData[j];

        const sharedFusions = fusionData.filter(fusion => {
          const rncps = fusion.sources.map(s => s.rncp);
          return rncps.includes(formation1.rncpCode) && rncps.includes(formation2.rncpCode);
        });

        if (sharedFusions.length > 0) {
          formationPairs.push({
            formation1,
            formation2,
            sharedFusionCount: sharedFusions.length,
            sharedFusions
          });
        }
      }
    }

    if (formationPairs.length === 0) {
      setStep('analysis');
      toast.info('Aucune compétence commune entre les formations');
      return;
    }

    formationPairs.sort((a, b) => b.sharedFusionCount - a.sharedFusionCount);
    const topPair = formationPairs[0];

    const totalCompetences1 = topPair.formation1.competences.length;
    const totalCompetences2 = topPair.formation2.competences.length;
    const fusedCount = topPair.sharedFusionCount;
    const unificationRate = Math.round((fusedCount / Math.min(totalCompetences1, totalCompetences2)) * 100);
    const timeSaved = fusedCount * 4;

    setComparisonData({
      formation1: topPair.formation1,
      formation2: topPair.formation2,
      sharedFusions: topPair.sharedFusions,
      fusedCount,
      unificationRate,
      timeSaved,
      totalFormations: enrichedData.length
    });

    setStep('comparison');
    toast.success(`✨ ${fusedCount} compétences communes identifiées !`);
  };

  // Rendu du contenu selon l'étape
  const renderContent = () => {
    switch (step) {
      case 'welcome':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Building2 className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Analysez vos formations
              </h2>
              <p className="text-gray-600">
                Identifiez automatiquement les opportunités de fusion
              </p>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg p-6 border border-blue-100">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Search className="w-5 h-5 text-blue-600" />
                Rechercher votre organisme
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Entrez le SIRET de votre organisme de formation pour commencer l'analyse
              </p>

              <div className="space-y-3">
                <input
                  type="text"
                  value={siret}
                  onChange={(e) => setSiret(formatSiret(e.target.value))}
                  placeholder="000 000 000 00000"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center text-lg tracking-wider"
                  maxLength={17}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearchOrganism()}
                />

                <button
                  onClick={handleSearchOrganism}
                  disabled={loading || siret.replace(/\s/g, '').length !== 14}
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-6 rounded-lg font-medium hover:from-blue-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Recherche en cours...
                    </>
                  ) : (
                    <>
                      <Search className="w-5 h-5" />
                      Rechercher
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        );

      case 'formations':
        return (
          <div className="space-y-6 max-h-[70vh] overflow-y-auto px-2">
            <div className="text-center sticky top-0 bg-white pb-4 z-10">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {formations.length} formations trouvées
              </h2>
              <p className="text-gray-600">
                Vérifiez les codes RNCP suggérés
              </p>
            </div>

            {formations.map((formation, index) => (
              <div key={index} className="bg-white border-2 border-gray-200 rounded-lg p-4">
                <h3 className="font-bold text-gray-900 mb-2">{formation.title}</h3>

                {editingRncp === index ? (
                  <div className="flex gap-2 mt-2">
                    <input
                      type="text"
                      value={manualRncp}
                      onChange={(e) => setManualRncp(e.target.value)}
                      placeholder="Code RNCP"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded"
                    />
                    <button
                      onClick={() => handleSaveRncp(index)}
                      className="px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                    >
                      <Save className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setEditingRncp(null)}
                      className="px-3 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between mt-2">
                    <div>
                      {formation.suggestedRncp ? (
                        <div className="flex items-center gap-2">
                          <span className="px-3 py-1 bg-green-100 text-green-800 rounded font-mono text-sm">
                            {formation.suggestedRncp}
                          </span>
                          <span className="text-sm text-gray-600">
                            Score: {Math.round(formation.score)}%
                          </span>
                        </div>
                      ) : (
                        <span className="text-red-600 text-sm">Aucun RNCP trouvé</span>
                      )}
                    </div>
                    <button
                      onClick={() => handleEditRncp(index)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            ))}

            <div className="sticky bottom-0 bg-white pt-4">
              <button
                onClick={handleEnrichFormations}
                disabled={loading || formations.filter(f => f.suggestedRncp).length === 0}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-6 rounded-lg font-medium hover:from-blue-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Enrichissement...
                  </>
                ) : (
                  <>
                    <ArrowRight className="w-5 h-5" />
                    Lancer l'analyse ({formations.filter(f => f.suggestedRncp).length} formations)
                  </>
                )}
              </button>
            </div>
          </div>
        );

      case 'enrichment':
      case 'analysis':
        return (
          <div className="space-y-6 py-8">
            <div className="text-center">
              <Loader2 className="w-16 h-16 text-blue-600 animate-spin mx-auto mb-6" />
              <h2 className="text-2xl font-bold mb-4">Analyse en cours...</h2>
              <p className="text-gray-600">
                {step === 'enrichment' ? 'Enrichissement des formations' : 'Analyse des fusions'}
              </p>
            </div>

            {enrichedFormations.length > 0 && (
              <div className="p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>{enrichedFormations.length}</strong> formations enrichies
                </p>
                <p className="text-sm text-blue-700 mt-1">
                  <strong>{enrichedFormations.reduce((sum, f) => sum + f.competences.length, 0)}</strong> compétences analysées
                </p>
              </div>
            )}
          </div>
        );

      case 'comparison':
        return comparisonData ? (
          <div className="space-y-6">
            {/* Métriques */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200 text-center">
                <div className="text-3xl font-bold text-blue-600">{comparisonData.fusedCount}</div>
                <div className="text-xs text-gray-700 mt-1">Compétences fusionnées</div>
              </div>
              <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg p-4 border border-purple-200 text-center">
                <div className="text-3xl font-bold text-purple-600">{comparisonData.unificationRate}%</div>
                <div className="text-xs text-gray-700 mt-1">Taux d'unification</div>
              </div>
              <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-lg p-4 border border-green-200 text-center">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <Clock className="w-5 h-5 text-green-600" />
                  <div className="text-3xl font-bold text-green-600">{comparisonData.timeSaved}h</div>
                </div>
                <div className="text-xs text-gray-700">Temps gagné</div>
              </div>
            </div>

            {/* CTA */}
            <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-lg p-6 border-2 border-green-300 text-center">
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Prêt à optimiser vos formations ?
              </h3>
              <p className="text-gray-700 mb-4">
                Découvrez nos offres et commencez à économiser du temps dès aujourd'hui
              </p>
              <button
                onClick={() => navigate('/pricing')}
                className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold shadow-lg hover:shadow-xl transition-all"
              >
                <Sparkles className="w-5 h-5" />
                Voir les offres
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>

            {/* Détails en bas */}
            <div className="text-sm text-gray-600 text-center">
              <p>Formations comparées :</p>
              <p className="font-semibold text-gray-900 mt-1">{comparisonData.formation1.title}</p>
              <p className="font-semibold text-gray-900">{comparisonData.formation2.title}</p>
            </div>
          </div>
        ) : null;

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-h-[90vh] overflow-hidden flex max-w-6xl">

        {/* Partie gauche: Galerie (2/3) */}
        <div className="hidden lg:block lg:w-2/3 bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 relative overflow-hidden">
          <div className="absolute inset-0 bg-black/10"></div>

          {/* Slides animées */}
          <div className="relative h-full flex items-center justify-center p-12">
            {slides.map((slide, idx) => (
              <div
                key={idx}
                className={`absolute inset-0 flex flex-col items-center justify-center p-12 transition-opacity duration-1000 ${
                  currentSlide === idx ? 'opacity-100' : 'opacity-0'
                }`}
              >
                <div className="w-64 h-64 bg-white/20 rounded-full flex items-center justify-center mb-8 backdrop-blur-sm">
                  <Award className="w-32 h-32 text-white/80" />
                </div>

                <h2 className="text-4xl font-bold text-white text-center mb-4">
                  {slide.title}
                </h2>
                <p className="text-xl text-white/90 text-center max-w-md">
                  {slide.description}
                </p>
              </div>
            ))}
          </div>

          {/* Indicateurs de slides */}
          <div className="absolute bottom-8 left-0 right-0 flex justify-center gap-2">
            {slides.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentSlide(idx)}
                className={`w-2 h-2 rounded-full transition-all ${
                  currentSlide === idx ? 'bg-white w-8' : 'bg-white/50'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Partie droite: Contenu (1/3) */}
        <div className="lg:w-1/3 w-full p-8 overflow-y-auto relative">
          {/* Bouton fermer */}
          <button
            onClick={() => navigate('/dashboard')}
            className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 transition-colors rounded-lg hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Contenu dynamique */}
          <div className="mt-8">
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Onboarding3;
